import { useEffect } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { PosDocumentForm } from "@/components/pos-documents/PosDocumentForm";
import { RequireAuth } from "@/components/RequireAuth";
import { useCompany } from "@/lib/api/companies.queries";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute("/companies/$companyId/pos/new")({
  component: NewPosDocumentPage,
});

function NewPosDocumentPage() {
  return (
    <RequireAuth>
      <AppShell>
        <NewPosDocumentContent />
      </AppShell>
    </RequireAuth>
  );
}

function NewPosDocumentContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const company = useCompany(companyId);

  useEffect(() => {
    setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  if (company.isPending) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }
  if (company.isError || !company.data) {
    return (
      <ErrorState
        title={t("pos.list.error_title")}
        description={t("pos.list.error_body")}
        error={company.error}
        onRetry={() => void company.refetch()}
        retryLabel={t("common.retry")}
      />
    );
  }
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="pos_electronico" />
      <div>
        <Link
          to="/companies/$companyId/pos"
          params={{ companyId }}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("pos.new.back")}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight">{t("pos.new.title")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("pos.new.subtitle")}</p>
      </div>
      <PosDocumentForm companyId={companyId} />
    </div>
  );
}
